import { useState, type ReactElement } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { EmojiPicker } from "@shared/ui/EmojiPicker";
import { IconEmoji } from "@shared/ui/icons";

/**
 * Кнопка со знаками для однострочного поля.
 *
 * Отдельно от той, что стоит в поле ввода сообщения: там панель прижата к краю
 * поля и живёт внутри его разметки, а здесь она нужна нескольким экранам сразу
 * — имени канала при создании и при правке. Две копии разошлись бы на первой
 * же правке вида.
 *
 * Кастомных знаков сервера здесь нет намеренно. Они хранятся видом `:имя:` и
 * превращаются в картинку только там, где текст разбирается разметкой. В имени
 * канала разбора нет, и такой знак остался бы двоеточиями на виду.
 */
export function EmojiPick({
  onPick,
  title = "Знак",
  trigger,
  align = "end",
}: {
  onPick: (value: string) => void;
  title?: string;
  /** Своя кнопка вместо значка в поле — например, плитка значка роли. */
  trigger?: ReactElement;
  /** К какому краю кнопки прижать панель: у поля — к правому, у плитки слева — к левому. */
  align?: "start" | "end";
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <button type="button" className="input-emoji" title={title} aria-label={title}>
            <IconEmoji size={14} />
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align={align}
        sideOffset={6}
        aria-label="Выбор знака"
        /*
         * Фокус ставим сами — на поиск внутри панели, а не на саму панель:
         * ищут здесь чаще, чем листают.
         */
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <EmojiPicker
          custom={[]}
          onPick={(value) => {
            onPick(value);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Куда именно лёг бы знак: в место каретки, а не в конец.
 *
 * Знак в имени канала обычно ставят первым (`🎮-игры`), и приписывать его в
 * конец значило бы заставлять переносить руками. Возвращает готовую строку и
 * место, куда после вставки поставить каретку.
 */
export function insertInto(
  element: HTMLInputElement | null,
  value: string,
  addition: string,
): { next: string; caret: number } {
  const at = element?.selectionStart ?? value.length;

  return {
    next: `${value.slice(0, at)}${addition}${value.slice(at)}`,
    caret: at + addition.length,
  };
}
